import { PlusCircle, X } from '@untitledui/icons';
import React, { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type {
  FilterCondition,
  FilterConditionGroup,
} from '@/lib/crm/filters/types';
import type { FieldTypeSchema } from '@/lib/crm/hooks/useFilterSchema';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import { FilterConditionRow } from './FilterConditionRow';
import { MoveConditionMenu } from './MoveConditionMenu';
import type { FieldSection, GroupSummary } from './shared';

interface GroupCardProps {
  group: FilterConditionGroup;
  groupIndex: number;
  totalGroups: number;
  allowGroups: boolean;
  fieldSections: FieldSection[];
  fieldFilter: (textValue: string, inputValue: string) => boolean;
  propertyTypeMap: Record<string, PropertyTypeSchema>;
  fieldTypeMap: Record<string, FieldTypeSchema>;
  associationTypeLookup: Record<string, AssociationType>;
  targetPropertyMaps: Record<string, Record<string, PropertyTypeSchema>>;
  teamId: number;
  invalidConditionIds: Set<string>;
  onUpdateCondition: (id: string, updates: Partial<FilterCondition>) => void;
  onRemoveCondition: (id: string) => void;
  onKeyDown: React.KeyboardEventHandler;
  onAssociationObjectsChange?: (objects: Record<string, CrmObject>) => void;
  onAddCondition: (groupId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onMoveCondition: (conditionId: string, targetGroupId: string) => void;
  groupSummaries: GroupSummary[];
  addFilterDisabled: boolean;
}

/**
 * One AND-group of conditions. When `allowGroups` is false, renders without a
 * card border/group controls so the DOM matches the pre-group flat layout.
 */
export const GroupCard = React.memo(function GroupCard({
  group,
  groupIndex,
  totalGroups,
  allowGroups,
  fieldSections,
  fieldFilter,
  propertyTypeMap,
  fieldTypeMap,
  associationTypeLookup,
  targetPropertyMaps,
  teamId,
  invalidConditionIds,
  onUpdateCondition,
  onRemoveCondition,
  onKeyDown,
  onAssociationObjectsChange,
  onAddCondition,
  onRemoveGroup,
  onMoveCondition,
  groupSummaries,
  addFilterDisabled,
}: GroupCardProps) {
  const handleAddFilter = useCallback(
    () => onAddCondition(group.id),
    [onAddCondition, group.id]
  );
  const handleRemoveGroup = useCallback(
    () => onRemoveGroup(group.id),
    [onRemoveGroup, group.id]
  );
  const otherGroups = useMemo(
    () => groupSummaries.filter(summary => summary.id !== group.id),
    [groupSummaries, group.id]
  );

  const showGroupControls = allowGroups && totalGroups > 1;

  return (
    <div
      role={allowGroups ? 'group' : undefined}
      aria-label={allowGroups ? `Group ${groupIndex + 1}` : undefined}
      data-component='filter-group'
      data-instance={allowGroups ? `group-${groupIndex}` : undefined}
      className={cx(
        'space-y-2',
        allowGroups && 'rounded-lg border border-secondary p-2.5'
      )}
    >
      {group.conditions.map((condition, conditionIndex) => {
        const row = (
          <FilterConditionRow
            condition={condition}
            fieldSections={fieldSections}
            fieldFilter={fieldFilter}
            propertyTypeMap={propertyTypeMap}
            fieldTypeMap={fieldTypeMap}
            associationTypeLookup={associationTypeLookup}
            targetPropertyMaps={targetPropertyMaps}
            teamId={teamId}
            invalidConditionIds={invalidConditionIds}
            onUpdate={onUpdateCondition}
            onRemove={onRemoveCondition}
            onKeyDown={onKeyDown}
            onAssociationObjectsChange={onAssociationObjectsChange}
          />
        );
        return (
          <React.Fragment key={`${group.id}:${condition.id}`}>
            {showGroupControls ? (
              <div className='flex gap-1 items-start'>
                <div className='flex-1 min-w-0'>{row}</div>
                <MoveConditionMenu
                  conditionId={condition.id}
                  otherGroups={otherGroups}
                  onMove={onMoveCondition}
                />
              </div>
            ) : (
              row
            )}
            {/* Left-aligned boolean token (not a centered float): the group
                reads top-to-bottom as "condition AND condition". */}
            {allowGroups &&
              group.conditions.length >= 2 &&
              conditionIndex < group.conditions.length - 1 && (
                <p className='text-xs font-medium tracking-wide text-quaternary pl-1'>
                  AND
                </p>
              )}
          </React.Fragment>
        );
      })}

      <div className='flex items-center gap-2'>
        <Button
          color='tertiary'
          size='sm'
          iconLeading={PlusCircle}
          onClick={handleAddFilter}
          isDisabled={addFilterDisabled}
        >
          Add filter
        </Button>
        {/* Right-aligned so it reads as the CARD's control (remove this
            group), not a sibling of Add filter. */}
        {showGroupControls && (
          <Button
            color='tertiary'
            size='sm'
            iconLeading={X}
            onClick={handleRemoveGroup}
            aria-label={`Remove group ${groupIndex + 1}`}
            data-component='remove-filter-group'
            data-instance={`group-${groupIndex}`}
            className='ml-auto'
          />
        )}
      </div>
    </div>
  );
});
